import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandItem, CommandList } from "@/components/ui/command";
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerTrigger,
} from "@/components/ui/drawer";
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import PaginationButton from "@/components/ui/pagination-button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    TableCell as CTableCell,
    TableHead as CTableHead,
    Table,
    TableBody,
    TableHeader,
    TableLoading,
    TableRow,
} from "@/components/ui/table";
import { tableCellClass, tableCellLastRowClass, tableHeadClass } from "@/components/ui/table-styles";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/components/ui/use-toast";
import { pharmacyClient } from "@/connect";
import { ItemType } from "@/gen/inventory/common_pb";
import { searchInventory } from "@/gen/pharmacy-PharmacyService_connectquery";
import { SearchInventoryRequest } from "@/gen/search/search_inventory_pb";
import { cn } from "@/lib/utils";
import { Route as InventoryAvailableRoute } from "@/routes/(pharma)/_layout/inventory/available-inventory/index";
import { convertToCurrencyRounded } from "@/utils/currency_converter";
import getPharmacyID from "@/utils/pharmacy_code";
import { useMutation } from "@connectrpc/connect-query";
import {
    CaretSortIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    DoubleArrowLeftIcon,
    DoubleArrowRightIcon,
    MixerHorizontalIcon,
} from "@radix-ui/react-icons";
import { useNavigate } from "@tanstack/react-router";
import { useDebounce } from "@uidotdev/usehooks";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { PlusCircleIcon, SearchIcon, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useInventoryData } from "../-hooks/useInventoryData";
import BarcodeModal from "../../add-stock/-components/BarcodeModal";
dayjs.extend(customParseFormat);

const TableCell = ({ className = "", ...props }) => (
  <CTableCell className={cn(tableCellClass, "py-0", className)} {...props} />
);
const TableHead = ({ className = "", ...props }) => (
  <CTableHead className={cn(tableHeadClass, "", className)} {...props} />
);

// Define column type
interface Column {
  key: string;
  label: string;
}

const Component = () => {
  const searchParams = InventoryAvailableRoute.useSearch();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState(searchParams.search || '');

  // Use the custom hook for data fetching
  const { data, isLoading, error, isFetching } = useInventoryData();

  // Local loading state for search actions
  const [isSearching, setIsSearching] = useState(false);

  // Barcode generation states - FIXED: Added showSearchResults and changed selectedBatch to selectedBatchIndex
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [barcodeSearch, setBarcodeSearch] = useState("");
  const [barcodeSearchResults, setBarcodeSearchResults] = useState<any[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false); // NEW: Control dropdown visibility
  const [selectedProduct, setSelectedProduct] = useState<any | null>(null);
  const [productBatches, setProductBatches] = useState<any[]>([]);
  const [selectedBatchIndex, setSelectedBatchIndex] = useState<string>("");
  const [customBarcodeGenerating, setCustomBarcodeGenerating] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<any | null>(null);
  const [isSelectingProduct, setIsSelectingProduct] = useState(false);
  const [generatedBarcodeData, setGeneratedBarcodeData] = useState<{
    barcode: string;
    productName: string;
    batchNumber: string;
    expiryDate: any;
    unit: string;
    unitType: string;
  } | null>(null);
  const debouncedBarcodeSearch = useDebounce(barcodeSearch, 300);

  // Search mutation for barcode product lookup - FIXED: Show results when successful
  const barcodeSearchMutation = useMutation(searchInventory, {
    onSuccess: (searchData) => {
      setBarcodeSearchResults(searchData.items);
      setShowSearchResults(true); // NEW: Show dropdown
    },
    onError: (error) => {
      toast({
        title: "Error",
        variant: "destructive",
        description: "Failed to search for items. Please try again.",
        duration: 5000,
      });
    },
  });

  // Search for products when user types in barcode drawer
  useEffect(() => {
    if (isSelectingProduct) {
      setIsSelectingProduct(false);
      return;
    }
    
    if (debouncedBarcodeSearch.length < 3) {
      setBarcodeSearchResults([]);
      setShowSearchResults(false);
      return;
    }
  
    const inputData = new SearchInventoryRequest();
    inputData.onlyAvailable = false;
    inputData.query = debouncedBarcodeSearch;
    inputData.limit = 10;
    barcodeSearchMutation.mutate(inputData);
  }, [debouncedBarcodeSearch]);

  // Filter batches from current page data when product is selected
  useEffect(() => {
    if (selectedProduct && data?.items) {
      const batches = data.items.filter((item: any) => {
        // Strategy 1: Match by masterId
        const masterIdMatch = item.masterId === selectedProduct.id;
        
        // Strategy 2: Match by tradeName (case-insensitive, trimmed)
        const itemTradeName = item.tradeName?.toLowerCase().trim();
        const selectedTradeName = selectedProduct.tradeName?.toLowerCase().trim();
        const tradeNameMatch = itemTradeName === selectedTradeName;
        
        // Type must always match
        const typeMatch = item.itemType === selectedProduct.type;
        
        // Match if EITHER masterId OR tradeName matches, AND type matches
        return (masterIdMatch || tradeNameMatch) && typeMatch;
      });
      
      setProductBatches(batches);
  
      if (batches.length === 0) {
        toast({
          title: "No Batches Found",
          variant: "default",
          description: "No batches found for this product on the current page. The search result ID may not match inventory data.",
          duration: 5000,
        });
      } 
    } else {
      setProductBatches([]);
    }
  }, [selectedProduct, data]);

  // FIXED: Handle batch selection with index
  const handleBatchSelect = (index: string) => {
    setSelectedBatchIndex(index);
    const batch = productBatches[parseInt(index)];
    setSelectedBatch(batch);
  };

  // FIXED: Reset drawer state when closing - added new states
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setBarcodeSearch("");
    setBarcodeSearchResults([]);
    setSelectedProduct(null);
    setProductBatches([]);
    setSelectedBatchIndex("");
    setSelectedBatch(null);
    setShowSearchResults(false);
    setGeneratedBarcodeData(null);
    setCustomBarcodeGenerating(false);
  };

  // Reset search loading when query completes
  useEffect(() => {
    if (!isFetching && isSearching) {
      setIsSearching(false);
    }
  }, [isFetching, isSearching]);

  // Helper function to update search params without full navigation
  const updateSearchParams = (updates: Partial<typeof searchParams>, showSearchLoading = false) => {
    if (showSearchLoading) {
      setIsSearching(true);
    }

    navigate({
      search: (prev: any) => ({ ...prev, ...updates }),
      replace: true,
    });
  };

  // Sorting state
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<string>("expiry_date");

  // Sort function
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Sorting data
  const sortedData = data?.items ? data.items.slice().sort((a: any, b: any) => {
    if (orderBy === "expiry_date" || orderBy === "last_sold_date") {
      const getDateValue = (item: any, key: string) => {
        const value = item[key];
        if (!value) return 0;
        if (typeof value === "string") return new Date(value).getTime();
        if (value.Time) return new Date(value.Time).getTime();
        return 0;
      };
      const dateA = getDateValue(a, orderBy);
      const dateB = getDateValue(b, orderBy);
      return order === "asc" ? dateA - dateB : dateB - dateA;
    }
    return 0;
  }) : [];

  const itemsPerPage = searchParams.pageSize;
  const totalPages = data?.totalCount 
    ? Math.ceil(data.totalCount / itemsPerPage) 
    : 1;

  const columns: Column[] = [
    { key: "trade_name", label: "Trade Name" },
    { key: "available_stock", label: "Available Stock (Unit per stock)" },
    { key: "batch_number", label: "Batch Number" },
    { key: "expiry_date", label: "Expiry Date" },
    { key: "unit_type", label: "Unit Type" },
    { key: "barcode", label: "Barcode" },
    { key: "price", label: "Price" },
    { key: "cost", label: "Cost" },
  ];

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>)
  );


  return (
    <>
      <Drawer
        open={drawerOpen}
        onOpenChange={(open) => {
          if (!open) handleDrawerClose();
          setDrawerOpen(open);
        }}
        direction="right"
      >
        <DrawerContent className="focus-visible:outline-none w-fit min-w-[30vw] px-4">
          <DrawerClose asChild className="fixed inset-y-2 right-4">
            <Button className="h-fit aspect-square p-2 grid place-items-center bg-[#eceff8] hover:bg-[#eceff8]">
              <X className="text-[#667085] h-6 w-6" />
            </Button>
          </DrawerClose>

          {/* Drawer Content - Product Selection for Barcode */}
          <div className="w-full h-full overflow-auto py-6">
            <h2 className="text-lg font-semibold mb-4">Generate Barcode Sticker</h2>

            <form className="w-full flex flex-col gap-4" onSubmit={(e) => e.preventDefault()}>
              {/* Product Search - FIXED: Added onFocus handler and improved state management */}
              <div className="pr-4 pb-4 border-b-[0.5px]">
                <p className="text-sm font-semibold mt-4 mb-2">
                  Product Name<span className="text-red-400">*</span>
                </p>
                <div className="flex-1 relative">
                  <div className="border rounded-md px-2 flex items-center gap-2">
                    <Input
                      placeholder="Search for a product"
                      className="text-center border-0"
                      value={barcodeSearch}
                      onChange={(e) => {
                        setBarcodeSearch(e.target.value);
                        if (!e.target.value) {
                          setSelectedProduct(null);
                          setProductBatches([]);
                          setSelectedBatchIndex("");
                        }
                      }}
                      onFocus={() => {
                        // NEW: Show results on focus if available
                        if (barcodeSearch.length >= 3 && barcodeSearchResults.length > 0) {
                          setShowSearchResults(true);
                        }
                      }}
                    />
                    <Button
                      className="px-2 py-0 h-8"
                      variant={"outline"}
                      size={"sm"}
                      type="button"
                      onClick={() => {
                        setBarcodeSearch("");
                        setBarcodeSearchResults([]);
                        setShowSearchResults(false);
                        setSelectedProduct(null);
                        setProductBatches([]);
                        setSelectedBatchIndex("");
                      }}
                    >
                      Clear
                    </Button>
                  </div>

                  {/* Search Results Dropdown - FIXED: Better positioning and visibility control */}
                  {showSearchResults && barcodeSearchResults.length > 0 && (
                    <Command className="w-full absolute top-full mt-1 z-50 border rounded-md shadow-md bg-white">
                      <CommandList>
                        {barcodeSearchResults.map((result, index: number) => (
                        <CommandItem
                          key={index}
                          onSelect={() => {
                            // Set flag to prevent search re-trigger
                            setIsSelectingProduct(true);
                            
                            // Clear previous selections
                            setSelectedBatchIndex("");
                            setSelectedBatch(null);
                            setGeneratedBarcodeData(null);
                            
                            // Set new product
                            setSelectedProduct({
                              id: result.masterId || result.id,
                              type: result.itemType || (result.type === ItemType.MEDICAL ? ItemType.MEDICAL : ItemType.NON_MEDICAL),
                              tradeName: result.tradeName,
                              unitType: result.unitType,
                              unit: result.unit,
                            });
                            
                            // Set search text (won't trigger search due to flag)
                            setBarcodeSearch(result.tradeName);
                            setShowSearchResults(false);
                          }}
                        >
                            {result.tradeName} | {result.unit} {result.unitType}
                          </CommandItem>
                        ))}
                      </CommandList>
                    </Command>
                  )}

                  {/* Show "No results" only when searching and no results */}
                  {barcodeSearch.length >= 3 && !showSearchResults && barcodeSearchResults.length === 0 && !selectedProduct && (
                    <Command className="w-full absolute top-full mt-1 z-50 border rounded-md shadow-md bg-white">
                      <CommandList>
                        <CommandEmpty>No results found</CommandEmpty>
                      </CommandList>
                    </Command>
                  )}
                </div>
              </div>

              {/* Batch Selection - FIXED: Use controlled Select with value */}
              {selectedProduct && productBatches.length > 0 && (
              <div className="pr-4">
                <p className="text-sm font-semibold mb-2">
                  Select Batch<span className="text-red-400">*</span>
                </p>
                <Select value={selectedBatchIndex} onValueChange={handleBatchSelect}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a batch" />
                  </SelectTrigger>
                  <SelectContent>
                    {productBatches.map((batch, index) => (
                      <SelectItem key={index} value={index.toString()}>
                        Batch: {batch.batchNumber} - Exp: {batch.expiryDate ? dayjs(batch.expiryDate.toDate()).format("DD/MM/YYYY") : "N/A"} - Qty: {batch.availableQuantity}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedProduct && selectedBatch && (
              <div className="pr-4 pt-4 border-t-[0.5px]">
                <Button 
                  size={"sm"} 
                  onClick={async () => {
                    try {
                      setCustomBarcodeGenerating(true);
                      
                      // Generate barcode using API
                      let generatedBarcode = "";
                      if (selectedBatch.itemType === ItemType.MEDICAL) {
                        const response = await pharmacyClient.generateCustomBarcode({
                          masterId: selectedBatch.masterId,
                          itemType: ItemType.MEDICAL
                        });
                        generatedBarcode = response.barcode as string;
                      } else {
                        const response = await pharmacyClient.generateCustomBarcode({
                          masterId: selectedBatch.masterId,
                          itemType: ItemType.NON_MEDICAL
                        });
                        generatedBarcode = response.barcode as string;
                      }
                      
                      // Store generated data
                      setGeneratedBarcodeData({
                        barcode: generatedBarcode,
                        productName: selectedBatch.tradeName,
                        batchNumber: selectedBatch.batchNumber,
                        expiryDate: selectedBatch.expiryDate,
                        unit: selectedBatch.unit,
                        unitType: selectedBatch.unitType
                      });
                      
                      // Open barcode modal
                      setBarcodeModalOpen(true);
                      
                    } catch (e) {
                      toast({
                        title: "Error",
                        variant: "destructive",
                        description: "Failed to generate barcode",
                        duration: 5000,
                      });
                      console.error(e);
                    } finally {
                      setCustomBarcodeGenerating(false);
                    }
                  }} 
                  disabled={customBarcodeGenerating}
                >
                  {customBarcodeGenerating ? "Please Wait..." : "Generate & Print Barcode"}
                </Button>
              </div>
            )}
            </form>
          </div>
        </DrawerContent>

        {/* Main Content */}
        <div>
          <div className="flex items-center justify-between">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink
                    onClick={() => {
                      navigate({
                        to: "/inventory",
                      });
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    Inventory Overview
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>All Inventory</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <div className="grid px-4 py-2 h-10">
              <Tabs defaultValue={searchParams.type}>
                <TabsList>
                  <TabsTrigger
                    value={"MEDICAL"}
                    onClick={() => {
                      navigate({
                        search: {
                          ...searchParams,
                          type: "MEDICAL",
                        },
                      });
                    }}
                  >
                    Medical
                  </TabsTrigger>
                  <TabsTrigger
                    value={"NON_MEDICAL"}
                    onClick={() => {
                      navigate({
                        search: {
                          ...searchParams,
                          type: "NON_MEDICAL",
                        },
                      });
                    }}
                  >
                    Non Medical
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <DrawerTrigger asChild>
              <Button size={"sm"}>Generate Barcode</Button>
            </DrawerTrigger>
          </div>
          <hr className="my-4" />

          <div className="overflow-y-scroll h-screen pb-48">
            <div className="flex items-center justify-between mb-4">
              <div className="w-full flex items-center gap-2 justify-between">
                <div className="flex items-center gap-x-2">
                  <div className="relative w-72">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                      <SearchIcon className="w-4 h-4" />
                    </span>
                    <Input
                      placeholder="Search for item"
                      value={searchValue}
                      onChange={(e) => {
                        setSearchValue(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          updateSearchParams(
                            {
                              search: searchValue,
                              page: 1,
                            },
                            true
                          );
                        }
                      }}
                      className="pl-8 pr-8 w-full"
                    />
                    {searchValue && (
                      <Button
                        variant="ghost"
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 h-6 w-6 text-gray-400 hover:text-gray-700"
                        onClick={() => {
                          setSearchValue("");
                          updateSearchParams({
                            search: "",
                            page: 1,
                          });
                        }}
                      >
                        <span className="sr-only">Clear</span>
                        &#10005;
                      </Button>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    disabled={isSearching || isLoading}
                    onClick={() => {
                      updateSearchParams(
                        {
                          search: searchValue,
                          page: 1,
                        },
                        true
                      );
                    }}
                  >
                    {isSearching || isLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                        Searching...
                      </div>
                    ) : (
                      "Search"
                    )}
                  </Button>
                  <div className="flex items-center gap-x-2 ml-4">
                    <label htmlFor="pageSize" className="text-sm text-gray-600 whitespace-nowrap">
                      Items per page:
                    </label>
                    <Select
                      value={searchParams.pageSize?.toString() || '10'}
                      onValueChange={(value) => {
                        const newPageSize = parseInt(value);
                        updateSearchParams({
                          pageSize: newPageSize,
                          page: 1,
                        }, true);
                      }}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className={`h-8 ${searchParams.expiring_in_30_days ? "bg-primary text-white" : ""}`}
                    onClick={() => {
                      updateSearchParams({
                        expiring_in_30_days: !searchParams.expiring_in_30_days,
                        expired: false,
                        page: 1,
                      });
                    }}
                  >
                    <PlusCircleIcon className="mr-2 h-4 w-4" />
                    Expiring in 30 Days
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className={`h-8 ${searchParams.expired ? "bg-primary text-white" : ""}`}
                    onClick={() => {
                      updateSearchParams({
                        expired: !searchParams.expired,
                        expiring_in_30_days: false,
                        page: 1,
                      });
                    }}
                  >
                    <PlusCircleIcon className="mr-2 h-4 w-4" />
                    Expired
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8">
                        <MixerHorizontalIcon className="mr-2 h-4 w-4" />
                        View
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[200px]">
                      <DropdownMenuLabel>Toggle Columns</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {columns.map((col) => (
                        <DropdownMenuCheckboxItem
                          key={col.key}
                          checked={visibleColumns[col.key]}
                          onCheckedChange={() =>
                            setVisibleColumns((prev) => ({
                              ...prev,
                              [col.key]: !prev[col.key],
                            }))
                          }
                          disabled={col.key === "actions"}
                        >
                          {col.label || "Actions"}
                        </DropdownMenuCheckboxItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>

            <div className="mt-4 border rounded-lg bg-white overflow-hidden">
            {isLoading ? (
              <TableLoading columns={columns} visibleColumns={visibleColumns} />
            ) : !data?.items || data.items.length === 0 ? (
                <div className="flex items-center justify-center p-8">
                  <div className="text-sm text-gray-500">No entries found</div>
                </div>
              ) : (
                <>
              <Table className="text-sm">
                <TableHeader>
                  <TableRow className="text-sm">
                    {columns.map(
                      (col) =>
                        visibleColumns[col.key] && (
                          <TableHead
                            key={col.key}
                            onClick={
                              col.key === "expiry_date"
                                ? () => handleRequestSort("expiry_date")
                                : undefined
                            }
                            style={col.key === "expiry_date" ? { cursor: "pointer" } : {}}
                          >
                            <div className="flex items-center gap-2">
                              <span>{col.label}</span>
                              {col.key === "expiry_date" && orderBy === "expiry_date" && (
                                <CaretSortIcon
                                  className={`ml-2 h-5 w-5 ${order === "asc" ? "rotate-180" : ""}`}
                                />
                              )}
                            </div>
                          </TableHead>
                        )
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedData.map((item, index) => (
                    <TableRow key={index} className="font-medium cursor-pointer" 
                    onClick={() => {
                      navigate({
                        to: "/inventory/item/$type/$itemId",
                        params: {
                          type: item.itemType === ItemType.MEDICAL ? "MEDICAL" : "NON_MEDICAL",
                          itemId: item.masterId,
                        },
                        search: searchParams,
                      });
                    }}>
                      {visibleColumns["trade_name"] && (
                        <TableCell className={`py-0 ${tableCellClass}`}>{item.tradeName}</TableCell>
                      )}
                      {visibleColumns["available_stock"] && (
                        <TableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                          {item.availableQuantity} ({item.unit})
                        </TableCell>
                      )}
                      {visibleColumns["batch_number"] && (
                        <TableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                          {item.batchNumber}
                        </TableCell>
                      )}
                      {visibleColumns["expiry_date"] && (
                        <TableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                          {item.isValidExpiryDate ? (
                            dayjs(item.expiryDate?.toDate()).format("DD/MM/YYYY")
                          ) : (
                            <span className="text-red-500">Invalid Date</span>)}
                        </TableCell>
                      )}
                      {visibleColumns["unit_type"] && (
                        <TableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                          {item.unitType}
                        </TableCell>
                      )}
                      {visibleColumns["barcode"] && (
                        <TableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                          {item.barcode}
                        </TableCell>
                      )}
                      {visibleColumns["price"] && (
                        <TableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                          {convertToCurrencyRounded(item.sellingPrice)}
                        </TableCell>
                      )}
                      {visibleColumns["cost"] && (
                        <TableCell className={index === sortedData.length - 1 ? tableCellLastRowClass : ""}>
                          {convertToCurrencyRounded(item.costFromSupplier)}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
                    <p>{data.items && data.items.length > 0 ? data.totalCount : 0} <span className="text-gray-500 font-light">count</span></p>
                    <div className="flex items-center gap-1">
                      <PaginationButton
                        onClick={() => {
                          updateSearchParams({
                            page: 1,
                          });
                        }}
                        disabled={searchParams.page === 1}
                      >
                        <DoubleArrowLeftIcon className="h-4 w-4" />
                      </PaginationButton>
                      <PaginationButton
                        onClick={() => {
                          navigate({
                            search: {
                              ...searchParams,
                              page: searchParams.page - 1,
                            },
                          });
                        }}
                        disabled={searchParams.page === 1}
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </PaginationButton>
                      {(() => {
                        const getPageNumbers = () => {
                          const pages = [];
                          if (totalPages <= 7) {
                            for (let i = 1; i <= totalPages; i++) {
                              pages.push(i);
                            }
                          } else {
                            pages.push(1);
                            if (searchParams.page > 3) pages.push("...");
                            for (
                              let i = Math.max(2, searchParams.page - 1);
                              i <= Math.min(searchParams.page + 1, totalPages - 1);
                              i++
                            ) {
                              pages.push(i);
                            }
                            if (searchParams.page < totalPages - 2) pages.push("...");
                            pages.push(totalPages);
                          }
                          return pages;
                        };
                        return getPageNumbers().map((page, idx) =>
                          page === "..." ? (
                            <span key={`ellipsis-${idx}`} className="mx-1">
                              ...
                            </span>
                          ) : (
                            <PaginationButton
                              key={page}
                              active={searchParams.page === page}
                              onClick={() => {
                                navigate({
                                  search: {
                                    ...searchParams,
                                    page: typeof page === "number" ? page : searchParams.page,
                                  },
                                });
                              }}
                            >
                              <span className="text-sm">{page}</span>
                            </PaginationButton>
                          )
                        );
                      })()}
                      <PaginationButton
                        onClick={() => {
                          navigate({
                            search: {
                              ...searchParams,
                              page: searchParams.page + 1,
                            },
                          });
                        }}
                        disabled={searchParams.page === totalPages}
                      >
                        <ChevronRightIcon className="h-4 w-4" />
                      </PaginationButton>
                      <PaginationButton
                        onClick={() => {
                          navigate({
                            search: {
                              ...searchParams,
                              page: totalPages,
                            },
                          });
                        }}
                        disabled={searchParams.page === totalPages}
                      >
                        <DoubleArrowRightIcon className="h-4 w-4" />
                      </PaginationButton>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Drawer>

      {/* Barcode Modal - FIXED: Proper null checks with useMemo computed value */}
      {generatedBarcodeData && (
        <BarcodeModal
          open={barcodeModalOpen}
          onOpenChange={setBarcodeModalOpen}
          initialData={{
            pharmacyName: getPharmacyID(),
            productName: generatedBarcodeData.productName,
            expiryDate: generatedBarcodeData.expiryDate 
              ? dayjs(generatedBarcodeData.expiryDate.toDate()).format("YYYY-MM-DD")
              : new Date().toISOString().split('T')[0],
            barcodeNumber: generatedBarcodeData.barcode,
          }}
          onPrintComplete={(barcodeNumber) => {
            toast({
              title: "Success",
              variant: "default",
              description: "Barcode printed successfully!",
              duration: 3000,
            });
            handleDrawerClose();
          }}
        />
      )}
    </>
  );
};

export default Component;